import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/NavBar';
import UpgradeModal from '../components/UpgradeModal';
import apiClient from '../api/client';
import useWindowWidth from '../hooks/useWindowWidth';
import { CATEGORIES } from '../utils/helpers';
import {
  PRIMARY, BG_PAGE, BG_CARD, BORDER, TEXT_DARK, TEXT_MID, TEXT_LIGHT, TEXT_MUTED,
  btn, primaryBtn, input as inputStyle, label as labelStyle, formGroup,
} from '../styles/shared';
import { trackEvent } from '../utils/analytics';
import { useAuth } from '../App';

// T29: All AI tool cards use the brand teal accent (#01696F) for visual
// consistency. Previously each card had an arbitrary accent color which
// read as noise rather than meaningful categorization.
const TOOL_ACCENT = '#01696F';

const TOOLS = [
  {
    id: 'essay-brainstorm',
    emoji: '\uD83D\uDCDD',
    title: 'Essay Brainstorm',
    subtitle: 'Discover compelling essay themes from your journal',
    color: TOOL_ACCENT,
    returns: 'Returns: essay theme suggestions',
    needsData: true,
    hasInputs: false,
    endpoint: '/api/ai/essay-brainstorm',
    buttonLabel: 'Generate Essay Ideas',
  },
  {
    id: 'activity-description',
    emoji: '\uD83D\uDCCB',
    title: 'Activity Description',
    subtitle: 'Write polished Common App activity descriptions',
    color: TOOL_ACCENT,
    returns: 'Returns: 150-char description',
    needsData: false,
    hasInputs: true,
    endpoint: '/api/ai/activity-description',
    buttonLabel: 'Generate Description',
  },
  {
    id: 'auto-tag',
    emoji: '\uD83C\uDFF7\uFE0F',
    title: 'Auto-Tag',
    subtitle: 'Get smart tag suggestions for your entries',
    color: TOOL_ACCENT,
    returns: 'Returns: tag list',
    needsData: false,
    hasInputs: true,
    endpoint: '/api/ai/auto-tag',
    buttonLabel: 'Suggest Tags',
  },
  {
    id: 'weekly-reflection',
    emoji: '\uD83D\uDCA1',
    title: 'Weekly Reflection',
    subtitle: 'Get personalized reflection prompts from your recent entries',
    color: TOOL_ACCENT,
    returns: 'Returns: paragraph reflection',
    needsData: true,
    hasInputs: false,
    endpoint: '/api/ai/weekly-reflection',
    buttonLabel: 'Generate Prompts',
  },
  {
    id: 'resume-bullets',
    emoji: '\uD83D\uDCC4',
    title: 'Resume Builder',
    subtitle: 'Turn your activities into resume-ready bullet points',
    color: TOOL_ACCENT,
    returns: 'Returns: bullet list',
    needsData: false,            // doesn't need entries
    needsActivities: true,        // does need profile activities
    hasInputs: false,
    endpoint: '/api/ai/resume-bullets',
    buttonLabel: 'Generate Bullet Points',
  },
  {
    id: 'skills-identifier',
    emoji: '\uD83D\uDD0D',
    title: 'Skills Identifier',
    subtitle: 'Discover your transferable skills from your journal',
    color: TOOL_ACCENT,
    returns: 'Returns: skills list',
    needsData: true,
    needsActivities: true,        // also benefits from activities
    hasInputs: false,
    endpoint: '/api/ai/skills-identifier',
    buttonLabel: 'Identify Skills',
  },
];

const styles = {
  page: { minHeight: '100vh', backgroundColor: BG_PAGE },
  content: { maxWidth: '960px', margin: '0 auto', padding: '2rem 1.5rem' },
  heading: { fontSize: '1.6rem', fontWeight: '700', color: TEXT_DARK, marginBottom: '0.3rem' },
  subheading: { color: TEXT_LIGHT, fontSize: '0.95rem', marginBottom: '2rem' },
  grid: (isMobile) => ({
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: '1rem',
  }),
  card: (color, expanded, dimmed) => ({
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER}`,
    borderLeft: `4px solid ${color}`,
    borderRadius: '16px',
    padding: '1.25rem 1.5rem',
    cursor: expanded ? 'default' : 'pointer',
    transition: 'box-shadow 0.2s, transform 0.15s',
    boxShadow: expanded ? '0 4px 16px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)',
    position: 'relative',
    opacity: dimmed ? 0.7 : 1,
  }),
  proBadge: (bg) => ({
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
    backgroundColor: bg,
    color: '#ffffff',
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '0.2rem 0.55rem',
    borderRadius: '999px',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  }),
  cardHeader: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  cardEmoji: { fontSize: '1.5rem', flexShrink: 0 },
  cardTitle: { fontWeight: '700', color: TEXT_DARK, fontSize: '1rem', margin: 0 },
  cardSubtitle: { color: TEXT_LIGHT, fontSize: '0.85rem', marginTop: '0.2rem' },
  // T30: muted one-line indicator at the bottom of each card describing the
  // shape of the AI tool's output, so users can preview what to expect.
  cardReturns: { color: '#94a3b8', fontSize: '0.75rem', marginTop: '0.75rem' },
  expandedBody: { marginTop: '1.25rem', borderTop: `1px solid ${BORDER}`, paddingTop: '1.25rem' },
  generateBtn: (color, loading) => ({
    ...btn,
    ...primaryBtn,
    backgroundColor: color,
    opacity: loading ? 0.7 : 1,
    width: '100%',
    marginTop: '0.75rem',
  }),
  errorMsg: { color: '#dc2626', fontSize: '0.85rem', marginTop: '0.5rem', padding: '0.5rem 0.75rem', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' },
  noDataMsg: { color: TEXT_LIGHT, fontSize: '0.875rem', padding: '1rem', textAlign: 'center', backgroundColor: '#f8fafc', borderRadius: '8px', border: `1px solid ${BORDER}` },
  resultSection: { marginTop: '1rem' },
  // Essay results
  themeCard: {
    backgroundColor: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '10px',
    padding: '1rem', marginBottom: '0.75rem',
  },
  themeTitle: { fontWeight: '700', color: '#7c3aed', fontSize: '0.95rem', marginBottom: '0.4rem' },
  themeRationale: { fontSize: '0.85rem', color: TEXT_MID, lineHeight: 1.55, marginBottom: '0.5rem' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem' },
  chip: (color) => ({
    display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '999px',
    backgroundColor: color + '1a', color: color, fontSize: '0.75rem', fontWeight: '600',
  }),
  // Activity description results
  descriptionResult: {
    backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px',
    padding: '1rem', marginBottom: '0.5rem',
  },
  descriptionText: { fontSize: '0.9rem', color: TEXT_DARK, lineHeight: 1.5, marginBottom: '0.5rem' },
  charCount: { fontSize: '0.75rem', color: TEXT_MUTED },
  copyBtn: {
    ...btn,
    padding: '0.4rem 0.75rem',
    minHeight: '36px',
    fontSize: '0.8rem',
    backgroundColor: BG_CARD,
    color: PRIMARY,
    border: `1px solid ${PRIMARY}`,
  },
  // Tag results
  tagChip: {
    display: 'inline-block', padding: '0.3rem 0.75rem', borderRadius: '999px',
    backgroundColor: '#f0fdfa', color: PRIMARY, fontSize: '0.8rem', fontWeight: '600',
    border: `1px solid ${PRIMARY}33`,
    marginRight: '0.4rem', marginBottom: '0.4rem',
  },
  // Reflection results
  reflectionCard: {
    backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px',
    padding: '1rem', marginBottom: '0.75rem',
  },
  reflectionPrompt: { fontWeight: '600', color: '#c2410c', fontSize: '0.9rem', marginBottom: '0.4rem' },
  reflectionContext: { fontSize: '0.85rem', color: TEXT_MID, lineHeight: 1.5 },
  // Resume results
  resumeGroup: {
    backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px',
    padding: '1rem', marginBottom: '0.75rem',
  },
  resumeActivity: { fontWeight: '700', color: '#16a34a', fontSize: '0.95rem', marginBottom: '0.5rem' },
  bulletItem: { fontSize: '0.85rem', color: TEXT_DARK, lineHeight: 1.55, marginBottom: '0.3rem', paddingLeft: '0.5rem' },
  // Skills results
  skillCard: {
    backgroundColor: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: '10px',
    padding: '1rem', marginBottom: '0.75rem',
  },
  skillHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' },
  skillName: { fontWeight: '700', color: '#be185d', fontSize: '0.95rem' },
  strengthBadge: (level) => {
    const colors = { strong: '#16a34a', developing: '#eab308', emerging: '#94a3b8' };
    const c = colors[level] || colors.emerging;
    return {
      display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '999px',
      backgroundColor: c + '1a', color: c, fontSize: '0.7rem', fontWeight: '700',
      textTransform: 'capitalize',
    };
  },
  skillEvidence: { fontSize: '0.85rem', color: TEXT_MID, lineHeight: 1.5, marginBottom: '0.3rem', paddingLeft: '0.5rem' },
  skillTip: { fontSize: '0.8rem', color: TEXT_LIGHT, fontStyle: 'italic', marginTop: '0.4rem' },
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={handleCopy} style={styles.copyBtn}>
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

function ActivityDescriptionInputs({ onSubmit, loading, color }) {
  const [form, setForm] = useState({ name: '', role: '', type: '', description: '', hoursPerWeek: '', weeksPerYear: '' });
  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div>
      <div style={formGroup}>
        <label style={labelStyle}>Activity Name *</label>
        <input style={inputStyle} value={form.name} onChange={update('name')} placeholder="e.g., Model United Nations" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div style={formGroup}>
          <label style={labelStyle}>Role</label>
          <input style={inputStyle} value={form.role} onChange={update('role')} placeholder="e.g., President" />
        </div>
        <div style={formGroup}>
          <label style={labelStyle}>Type</label>
          <input style={inputStyle} value={form.type} onChange={update('type')} placeholder="e.g., Club" />
        </div>
      </div>
      <div style={formGroup}>
        <label style={labelStyle}>Description</label>
        <textarea
          style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
          value={form.description}
          onChange={update('description')}
          placeholder="Briefly describe what you do..."
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div style={formGroup}>
          <label style={labelStyle}>Hours/Week</label>
          <input style={inputStyle} type="number" value={form.hoursPerWeek} onChange={update('hoursPerWeek')} placeholder="e.g., 5" />
        </div>
        <div style={formGroup}>
          <label style={labelStyle}>Weeks/Year</label>
          <input style={inputStyle} type="number" value={form.weeksPerYear} onChange={update('weeksPerYear')} placeholder="e.g., 40" />
        </div>
      </div>
      <button
        style={styles.generateBtn(color, loading)}
        onClick={() => onSubmit(form)}
        disabled={loading || !form.name.trim()}
      >
        {loading ? 'Generating...' : 'Generate Description'}
      </button>
    </div>
  );
}

function AutoTagInputs({ onSubmit, loading, color }) {
  const [form, setForm] = useState({ title: '', content: '', category: '' });
  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div>
      <div style={formGroup}>
        <label style={labelStyle}>Title *</label>
        <input style={inputStyle} value={form.title} onChange={update('title')} placeholder="Entry title" />
      </div>
      <div style={formGroup}>
        <label style={labelStyle}>Content *</label>
        <textarea
          style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
          value={form.content}
          onChange={update('content')}
          placeholder="Paste or type your entry content..."
        />
      </div>
      <div style={formGroup}>
        <label style={labelStyle}>Category (optional)</label>
        <select style={inputStyle} value={form.category} onChange={update('category')}>
          <option value="">Select a category...</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <button
        style={styles.generateBtn(color, loading)}
        onClick={() => onSubmit(form)}
        disabled={loading || !form.title.trim() || !form.content.trim()}
      >
        {loading ? 'Suggesting...' : 'Suggest Tags'}
      </button>
    </div>
  );
}

function EssayResults({ data }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  return (
    <div style={styles.resultSection}>
      {data.map((idea, idx) => (
        <div key={idx} style={styles.themeCard}>
          <div style={styles.themeTitle}>{idea.theme}</div>
          <p style={styles.themeRationale}>{idea.rationale}</p>
          {Array.isArray(idea.relatedEntries) && idea.relatedEntries.length > 0 && (
            <div style={styles.chipRow}>
              {idea.relatedEntries.map((entry, i) => (
                <span key={i} style={styles.chip('#8b5cf6')}>{entry}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ActivityDescriptionResults({ data }) {
  if (!data) return null;
  const desc = typeof data === 'string' ? data : data.description || '';
  if (!desc) return null;
  return (
    <div style={styles.resultSection}>
      <div style={styles.descriptionResult}>
        <p style={styles.descriptionText}>{desc}</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={styles.charCount}>{desc.length}/150 characters</span>
          <CopyButton text={desc} />
        </div>
      </div>
    </div>
  );
}

function AutoTagResults({ data }) {
  const tags = Array.isArray(data) ? data : (data?.tags || []);
  if (tags.length === 0) return null;
  return (
    <div style={styles.resultSection}>
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {tags.map((tag, i) => (
          <span key={i} style={styles.tagChip}>{tag}</span>
        ))}
      </div>
    </div>
  );
}

function WeeklyReflectionResults({ data }) {
  const prompts = Array.isArray(data) ? data : (data?.prompts || []);
  if (prompts.length === 0) return null;
  return (
    <div style={styles.resultSection}>
      {prompts.map((p, idx) => (
        <div key={idx} style={styles.reflectionCard}>
          <div style={styles.reflectionPrompt}>{p.prompt || p.title || p}</div>
          {p.context && <p style={styles.reflectionContext}>{p.context}</p>}
          {Array.isArray(p.relatedEntries) && p.relatedEntries.length > 0 && (
            <div style={{ ...styles.chipRow, marginTop: '0.5rem' }}>
              {p.relatedEntries.map((e, i) => (
                <span key={i} style={styles.chip('#f97316')}>{e}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ResumeBulletResults({ data }) {
  const groups = Array.isArray(data) ? data : (data?.activities || []);
  if (groups.length === 0) return null;
  return (
    <div style={styles.resultSection}>
      {groups.map((group, idx) => (
        <div key={idx} style={styles.resumeGroup}>
          <div style={styles.resumeActivity}>{group.activity || group.name}</div>
          {Array.isArray(group.bullets) && group.bullets.map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginBottom: '0.3rem' }}>
              <span style={{ color: '#22c55e', fontWeight: '700', flexShrink: 0 }}>&#x2022;</span>
              <span style={styles.bulletItem}>{b}</span>
            </div>
          ))}
          <div style={{ marginTop: '0.5rem' }}>
            <CopyButton text={(group.bullets || []).join('\n')} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SkillsResults({ data }) {
  const skills = Array.isArray(data) ? data : (data?.skills || []);
  if (skills.length === 0) return null;
  return (
    <div style={styles.resultSection}>
      {skills.map((skill, idx) => (
        <div key={idx} style={styles.skillCard}>
          <div style={styles.skillHeader}>
            <span style={styles.skillName}>{skill.name || skill.skill}</span>
            {skill.strength && <span style={styles.strengthBadge(skill.strength)}>{skill.strength}</span>}
          </div>
          {Array.isArray(skill.evidence) && skill.evidence.map((e, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginBottom: '0.2rem' }}>
              <span style={{ color: '#ec4899', flexShrink: 0 }}>&#x2013;</span>
              <span style={styles.skillEvidence}>{e}</span>
            </div>
          ))}
          {skill.tip && <p style={styles.skillTip}>{skill.tip}</p>}
        </div>
      ))}
    </div>
  );
}

export default function AiToolsPage() {
  const { isMobile } = useWindowWidth();
  const { subscriptionStatus } = useAuth() || {};
  const subStatus = subscriptionStatus?.status || 'free';
  const isFree = subStatus !== 'active' && subStatus !== 'trialing';
  const isTrialing = subStatus === 'trialing';
  const isActive = subStatus === 'active';
  const [upgradeModalFeature, setUpgradeModalFeature] = useState(null);
  const [expandedTool, setExpandedTool] = useState(null);
  const [results, setResults] = useState({});
  const [loadingTool, setLoadingTool] = useState(null);
  const [errors, setErrors] = useState({});

  // Shared data for tools that need profile/entries
  const [entries, setEntries] = useState(null);
  const [profile, setProfile] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const fetchUserData = useCallback(async () => {
    if (dataLoaded) return;
    setDataLoading(true);
    try {
      const [entriesRes, profileRes] = await Promise.all([
        apiClient.get('/api/entries?page=1&limit=100'),
        apiClient.get('/api/profile'),
      ]);
      setEntries(entriesRes.data.data?.entries || []);
      setProfile(profileRes.data.data);
      setDataLoaded(true);
    } catch {
      // Silently fail — individual tools will show error
    } finally {
      setDataLoading(false);
    }
  }, [dataLoaded]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleCardClick = (toolId) => {
    if (isFree) {
      setUpgradeModalFeature(toolId);
      return;
    }
    setExpandedTool(prev => prev === toolId ? null : toolId);
  };

  const handleGenerate = async (tool, inputData) => {
    setLoadingTool(tool.id);
    setErrors(prev => ({ ...prev, [tool.id]: null }));

    let payload = {};
    switch (tool.id) {
      case 'essay-brainstorm':
        payload = {
          entries: (entries || []).slice(0, 10),
          profile: {
            activities: profile?.activities || [],
            awards: profile?.awards || [],
          },
        };
        break;
      case 'activity-description':
        payload = inputData;
        break;
      case 'auto-tag':
        payload = inputData;
        break;
      case 'weekly-reflection':
        payload = {
          entries: (entries || []).slice(0, 10),
        };
        break;
      case 'resume-bullets':
        payload = {
          activities: profile?.activities || [],
          entries: (entries || []).slice(0, 20),
        };
        break;
      case 'skills-identifier':
        payload = {
          entries: (entries || []).slice(0, 20),
          activities: profile?.activities || [],
        };
        break;
      default:
        break;
    }

    try {
      const res = await apiClient.post(tool.endpoint, payload);
      const data = res.data?.data;
      if (res.data?.error) {
        setErrors(prev => ({ ...prev, [tool.id]: res.data.error }));
      } else {
        // Normalize response per tool
        let normalized = data;
        if (tool.id === 'essay-brainstorm') {
          normalized = data?.suggestions || data || [];
        } else if (tool.id === 'auto-tag') {
          normalized = data?.tags || data || [];
        } else if (tool.id === 'activity-description') {
          normalized = data?.description || data || '';
        } else if (tool.id === 'weekly-reflection') {
          normalized = data?.prompts || data || [];
        } else if (tool.id === 'resume-bullets') {
          normalized = data?.activities || data || [];
        } else if (tool.id === 'skills-identifier') {
          normalized = data?.skills || data || [];
        }
        setResults(prev => ({ ...prev, [tool.id]: normalized }));
        // used_ai_tool: fires only on a successful (non-error) AI tool call.
        trackEvent('used_ai_tool', { tool: tool.id });
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Something went wrong. Please try again.';
      setErrors(prev => ({ ...prev, [tool.id]: msg }));
    } finally {
      setLoadingTool(null);
    }
  };

  const hasNoEntries = dataLoaded && (!entries || entries.length === 0);
  const hasNoActivities = dataLoaded && (!profile?.activities || profile.activities.length === 0);

  const renderResults = (toolId) => {
    const data = results[toolId];
    if (!data) return null;
    switch (toolId) {
      case 'essay-brainstorm': return <EssayResults data={data} />;
      case 'activity-description': return <ActivityDescriptionResults data={data} />;
      case 'auto-tag': return <AutoTagResults data={data} />;
      case 'weekly-reflection': return <WeeklyReflectionResults data={data} />;
      case 'resume-bullets': return <ResumeBulletResults data={data} />;
      case 'skills-identifier': return <SkillsResults data={data} />;
      default: return null;
    }
  };

  const renderExpandedContent = (tool) => {
    const isLoading = loadingTool === tool.id;
    const error = errors[tool.id];

    // Tools that need profile activities but user has none
    if (tool.needsActivities && hasNoActivities) {
      return (
        <div style={styles.expandedBody}>
          <div style={styles.noDataMsg}>
            Add activities to your profile first to use this tool.{' '}
            <Link to="/profile" style={{ color: PRIMARY, fontWeight: '600' }}>Go to your profile</Link>
          </div>
        </div>
      );
    }

    // Tools that need journal entries but user has none
    if (tool.needsData && hasNoEntries) {
      return (
        <div style={styles.expandedBody}>
          <div style={styles.noDataMsg}>
            Add some journal entries first to use this tool.{' '}
            <Link to="/journal/new" style={{ color: PRIMARY, fontWeight: '600' }}>Write your first entry</Link>
          </div>
        </div>
      );
    }

    // Tools with manual inputs
    if (tool.id === 'activity-description') {
      return (
        <div style={styles.expandedBody}>
          <ActivityDescriptionInputs
            onSubmit={(data) => handleGenerate(tool, data)}
            loading={isLoading}
            color={tool.color}
          />
          {error && <div style={styles.errorMsg}>{error}</div>}
          {renderResults(tool.id)}
        </div>
      );
    }

    if (tool.id === 'auto-tag') {
      return (
        <div style={styles.expandedBody}>
          <AutoTagInputs
            onSubmit={(data) => handleGenerate(tool, data)}
            loading={isLoading}
            color={tool.color}
          />
          {error && <div style={styles.errorMsg}>{error}</div>}
          {renderResults(tool.id)}
        </div>
      );
    }

    // Tools that auto-load data
    return (
      <div style={styles.expandedBody}>
        {dataLoading && <p style={{ color: TEXT_LIGHT, fontSize: '0.85rem' }}>Loading your data...</p>}
        <button
          style={styles.generateBtn(tool.color, isLoading || dataLoading)}
          onClick={() => handleGenerate(tool)}
          disabled={isLoading || dataLoading}
        >
          {isLoading ? 'Generating...' : tool.buttonLabel}
        </button>
        {error && <div style={styles.errorMsg}>{error}</div>}
        {renderResults(tool.id)}
      </div>
    );
  };

  return (
    <div style={styles.page}>
      <NavBar />
      <div id="main-content" tabIndex={-1} style={styles.content}>
        <h1 style={styles.heading}>AI Tools</h1>
        <p style={styles.subheading}>Your AI-powered toolkit for every stage of high school</p>

        <div style={styles.grid(isMobile)}>
          {TOOLS.map(tool => {
            const expanded = expandedTool === tool.id;
            // Badge selection: active subscribers get nothing, trialing
            // sees an informational pill, free sees a Pro pill + dim.
            let badge = null;
            if (isActive) {
              badge = null;
            } else if (isTrialing) {
              badge = { label: 'Pro · Free during trial', bg: '#f59e0b' };
            } else {
              badge = { label: 'Pro', bg: '#64748b' };
            }
            return (
              <div
                key={tool.id}
                style={{
                  ...styles.card(tool.color, expanded, isFree),
                  ...(expanded && !isMobile ? { gridColumn: '1 / -1' } : {}),
                }}
                onClick={() => !expanded && handleCardClick(tool.id)}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick(tool.id);
                  }
                }}
              >
                {badge && (
                  <span style={styles.proBadge(badge.bg)} aria-label={badge.label}>
                    {badge.label}
                  </span>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={styles.cardHeader}>
                    <span style={styles.cardEmoji}>{tool.emoji}</span>
                    <div>
                      <h3 style={styles.cardTitle}>{tool.title}</h3>
                      <p style={styles.cardSubtitle}>{tool.subtitle}</p>
                    </div>
                  </div>
                  {expanded && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setExpandedTool(null); }}
                      aria-label={`Collapse ${tool.title}`}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: '1.2rem', color: TEXT_MUTED, padding: '0.25rem',
                        lineHeight: 1, flexShrink: 0,
                      }}
                    >
                      &#x2715;
                    </button>
                  )}
                </div>
                {!expanded && tool.returns && (
                  <div style={styles.cardReturns}>{tool.returns}</div>
                )}
                {expanded && (
                  <div onClick={(e) => e.stopPropagation()}>
                    {renderExpandedContent(tool)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <UpgradeModal
        isOpen={Boolean(upgradeModalFeature)}
        onClose={() => setUpgradeModalFeature(null)}
        feature={upgradeModalFeature || 'ai-tools'}
      />
    </div>
  );
}
